import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {LogicLanguage} from '../../../../../../../../core/shared/components/code-mirror-editor/code-mirror-editor.component';
import {MvsMessageService} from '../../../../../../../../core/shared/service/message/mvs-message.service';
import {CwExchangeConfigApiService} from '../../../../../../service/api/cw-exchange-config-api.service';
import {CwExchangeSchemaResponseDto} from '../../../../../../model/dto/api/exchange/cw-exchange-schema-response.dto';
import {
    CwExchangePreviewFailure,
    CwExchangePreviewFailureKind,
    classifyCwExchangePreviewFailure
} from '../../../../../../util/cw-exchange-preview-failure.util';

/**
 * AI:
 * Status: "in progress"
 * Type: Component
 * SubType: CrudIntegration
 * Reason: [CW-SCHEMA] "Schema" navigation point of the CwCaseType standard view — the read-only JSON
 *   of {@code GET /cw/exchange/schema} in a CodeMirror viewer. It is what a configurator reads before
 *   writing the TAA skeleton: which nodes exist, how many instances of each the configuration allows,
 *   which keys they publish with which template type, and which CODES the bound value lists accept.
 *
 *   Read-only on purpose, in both senses. The endpoint is side-effect-free, and the editor is pinned to
 *   {@code readOnly} because the CodeMirror wrapper only ADOPTS an {@code editorContent} that arrives
 *   after the view was built when read-only is set — an HTTP-loaded document in an editable editor stays
 *   invisible. {@code LogicLanguage.json} is a pure highlighting mode and must therefore always travel
 *   with {@code showLanguageSelector = false}; it is not offered in the wrapper's language list and
 *   picking it there would persist the wrong language on a logic block.
 */
@Component({
    selector: 'cw-case-type-schema',
    templateUrl: './cw-case-type-schema.component.html',
    styleUrls: ['./cw-case-type-schema.component.scss'],
    standalone: false
})
export class CwCaseTypeSchemaComponent implements OnInit, OnChanges {

    /** The CwCaseType whose schema is shown (access-checked server-side, on top of the role gate). */
    @Input() caseTypeId: number;

    /** Pinned highlighting mode of the viewer — see the class doc for why it may not be selectable. */
    readonly editorLanguage = LogicLanguage.json;

    busy = false;
    error?: string;
    /** Why {@link error} happened — decides whether it is painted as a refusal or as a failure. */
    errorKind?: CwExchangePreviewFailureKind;
    schema?: CwExchangeSchemaResponseDto;

    /** The formatted document handed to CodeMirror; rebuilt only when a response arrives. */
    schemaJson = '';

    constructor(
        private api: CwExchangeConfigApiService,
        private messageService: MvsMessageService) {
    }

    ngOnInit(): void {
        this.loadSchema();
    }

    /**
     * How the failure should be painted. A missing right is an EXPECTED outcome of a navigation point
     * that stays visible to everyone — a red banner would report the user's own role as a defect of the
     * system. Identical to the preview panel's getter on purpose: one refusal, one colour.
     */
    get errorSeverity(): 'warn' | 'error' {
        return this.errorKind === 'forbidden' ? 'warn' : 'error';
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['caseTypeId'] && !changes['caseTypeId'].firstChange) {
            this.loadSchema();
        }
    }

    // ============================================================================================
    // Header figures — the orientation a 4000-line document cannot give on its own
    // ============================================================================================

    get nodeCount(): number {
        return this.schema?.structure?.length ?? 0;
    }

    get valueListCount(): number {
        return this.schema?.valueLists?.length ?? 0;
    }

    get gaps(): string[] {
        return this.schema?.gaps ?? [];
    }

    /** A truncated value list publishes fewer entries than it holds — worth naming next to the count. */
    get truncatedValueListCount(): number {
        return (this.schema?.valueLists ?? []).filter(list => list.truncated).length;
    }

    get caseTypeLabel(): string {
        const caseType = this.schema?.meta?.caseType;
        if (!caseType) {
            return '';
        }
        return caseType.name ? `${caseType.name} (${caseType.id})` : `${caseType.id}`;
    }

    // ============================================================================================
    // Loading
    // ============================================================================================

    loadSchema(): void {
        if (this.caseTypeId == null) {
            this.schema = undefined;
            this.schemaJson = '';
            this.error = 'Es ist kein Antragstyp ausgewählt.';
            return;
        }
        this.busy = true;
        this.error = undefined;
        this.errorKind = undefined;
        this.api.getSchema(this.caseTypeId).subscribe({
            next: response => {
                this.busy = false;
                this.schema = response;
                this.schemaJson = JSON.stringify(response, null, 2);
            },
            error: (err: HttpErrorResponse) => {
                this.busy = false;
                this.schema = undefined;
                this.schemaJson = '';
                const failure = CwCaseTypeSchemaComponent.extractFailure(err);
                this.errorKind = failure.kind;
                this.error = failure.message;
            }
        });
    }

    /**
     * Copies the whole formatted schema to the clipboard.
     *
     * The purpose of this view is that someone writes a TAA skeleton from it — by hand or by pasting the
     * schema into an agent prompt. Select-all inside a CodeMirror document of several thousand lines is
     * exactly where that fails in practice, so the document is offered as one action. Modelled on
     * {@code MvsFreemarkerViewerComponent.copyToClipboard}, including the guard on {@code writeText}
     * itself: a clipboard object without the method throws SYNCHRONOUSLY, i.e. before any promise
     * exists, so a {@code .catch()} would never see it.
     */
    copyToClipboard(): void {
        if (!this.schemaJson) {
            return;
        }
        if (!navigator?.clipboard?.writeText) {
            this.messageService.showWarningMessage(
                'Nicht unterstützt',
                'Die Zwischenablage-API wird vom Browser nicht unterstützt.'
            );
            return;
        }
        navigator.clipboard.writeText(this.schemaJson)
            .then(() => this.messageService.showSuccessMessage(
                'Kopiert',
                'Das Schema wurde in die Zwischenablage kopiert.'
            ))
            .catch(() => this.messageService.showErrorMessage(
                'Fehler',
                'Kopieren in die Zwischenablage fehlgeschlagen.'
            ));
    }

    /**
     * Classifies a failed schema read — through the SHARED classifier, never a local envelope reader.
     *
     * <p>This method used to do the whole job itself, and that was the defect: it read the
     * {@code CoreApiResponseDto} envelope a third time (after {@code cw-api-error.util} and
     * {@code cw-exchange-preview-failure.util}) and carried its OWN German sentence for a 403, so the same
     * user hitting the same admin gate was told one thing here and another on the preview panel.
     * {@link classifyCwExchangePreviewFailure} now owns both: the 403 wording — which mirrors the backend
     * constant {@code CwControllerExceptionAdvice.ACCESS_DENIED_MESSAGE} — and the envelope reading.</p>
     *
     * <p>What stays local is only the FALLBACK, and only because it is status-specific: "the service is
     * not on this system" and "no connection" are worth saying, and the classifier cannot know either
     * sentence. It is handed in, so a message the server DID send still wins over it.</p>
     */
    private static extractFailure(err: HttpErrorResponse): CwExchangePreviewFailure {
        return classifyCwExchangePreviewFailure(err, CwCaseTypeSchemaComponent.fallbackFor(err?.status));
    }

    /** The German sentence to show when the server sent none, per status. */
    private static fallbackFor(status: number | undefined): string {
        if (status === 0) {
            return 'Das Schema konnte nicht geladen werden: keine Verbindung zum Server.';
        }
        if (status === 404) {
            return 'Das Schema ist auf diesem System nicht verfügbar. '
                + 'Vermutlich läuft hier noch ein Stand ohne den Schema-Dienst.';
        }
        return 'Das Schema konnte nicht geladen werden.';
    }
}
